import React from 'react';

type IconProps = { size?: number; className?: string };

export const IconBookmark: React.FC<IconProps> = ({ size = 16, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden
  >
    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
  </svg>
);

export const IconBookmarkFilled: React.FC<IconProps> = ({ size = 14, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    stroke="currentColor"
    strokeWidth="2"
    className={className}
    aria-hidden
  >
    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
  </svg>
);

export const IconFilterSliders: React.FC<IconProps> = ({ size = 16, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className={className}
    aria-hidden
  >
    <line x1="4" y1="6" x2="20" y2="6" />
    <line x1="8" y1="12" x2="20" y2="12" />
    <line x1="12" y1="18" x2="20" y2="18" />
  </svg>
);

export const IconFilterSlidersFab: React.FC<IconProps> = ({ size = 20, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    className={className}
    aria-hidden
  >
    <line x1="4" y1="6" x2="20" y2="6" />
    <line x1="8" y1="12" x2="20" y2="12" />
    <line x1="12" y1="18" x2="20" y2="18" />
  </svg>
);

export const IconSearch: React.FC<IconProps> = ({ size = 18, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.25"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden
  >
    <circle cx="11" cy="11" r="7" />
    <line x1="20" y1="20" x2="16.65" y2="16.65" />
  </svg>
);

export const IconCart: React.FC<IconProps> = ({ size = 16, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    className={className}
    aria-hidden
  >
    <circle cx="9" cy="21" r="1" />
    <circle cx="20" cy="21" r="1" />
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
  </svg>
);

export const IconCartFab: React.FC<IconProps> = ({ size = 22, className }) => (
  <IconCart size={size} className={className} />
);

export const stripTagEmoji = (tag: string): string =>
  tag.replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, '').trim();

export const IconPlus: React.FC<IconProps> = ({ size = 18, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    className={className}
    aria-hidden
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

export const IconLock: React.FC<IconProps> = ({ size = 14, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className={className}
    aria-hidden
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

export const IconCheck: React.FC<IconProps> = ({ size = 16, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/** Square veg / non-veg indicator (matches menu card dietary mark) */
export const IconDietaryMark: React.FC<IconProps & { nonVeg?: boolean }> = ({
  size = 15,
  className,
  nonVeg = false,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    className={className}
    aria-hidden
  >
    <rect
      x="1"
      y="1"
      width="14"
      height="14"
      rx="3"
      fill="#fff"
      stroke={nonVeg ? '#C0392B' : '#2E7D52'}
      strokeWidth="1.5"
    />
    <circle cx="8" cy="8" r="3.5" fill={nonVeg ? '#C0392B' : '#2E7D52'} />
  </svg>
);

/** Classic menu — single detailed card (image + title + price) */
export const IconMenuViewClassic: React.FC<IconProps> = ({ size = 18, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden
  >
    <rect x="4" y="2" width="16" height="20" rx="2.5" />
    <rect x="6.5" y="4.5" width="11" height="9" rx="1.5" fill="currentColor" stroke="none" opacity="0.12" />
    <line x1="7" y1="16" x2="17" y2="16" />
    <line x1="7" y1="19" x2="12.5" y2="19" />
  </svg>
);

/** Browse menu — compact grid with category rail */
export const IconMenuViewBrowse: React.FC<IconProps> = ({ size = 18, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden
  >
    <rect x="3" y="4" width="4" height="16" rx="1" />
    <rect x="10" y="4" width="4.5" height="4.5" rx="1" />
    <rect x="16.5" y="4" width="4.5" height="4.5" rx="1" />
    <rect x="10" y="10.5" width="4.5" height="4.5" rx="1" />
    <rect x="16.5" y="10.5" width="4.5" height="4.5" rx="1" />
    <rect x="10" y="17" width="4.5" height="4.5" rx="1" />
    <rect x="16.5" y="17" width="4.5" height="4.5" rx="1" />
  </svg>
);
